<template>
  <div>
    <div v-if="errorMessage" class="text-danger small mb-2">{{ errorMessage }}</div>
    <div class="loading" v-if="fetchData"><load-data></load-data></div>
    <div v-else>
      <div v-if="!students.length" class="text-muted small py-2">No students assigned to you for this round.</div>
      <div v-else-if="!questions.length" class="text-muted small py-2">This question paper has no questions yet.</div>
      <template v-else>
        <div v-if="resultPublished" class="text-muted small mb-2">The result is published; marks can no longer be edited.</div>
        <div v-else class="d-flex justify-content-end mb-2">
          <b-button variant="primary" size="sm" :disabled="saving" @click="save">
            {{ saving ? "Saving..." : "Save Scores" }}
          </b-button>
        </div>
        <div class="table-responsive">
          <table class="table table-bordered table-sm mb-0 evaluate-table">
            <thead class="thead-dark">
              <tr>
                <th colspan="2" class="align-middle">Question</th>
                <th v-for="question in displayQuestions" :key="'no-' + question.id" class="text-center align-middle">{{ question.question_no }}</th>
                <th colspan="2"></th>
              </tr>
              <tr>
                <th colspan="2" class="align-middle">Max Marks</th>
                <th v-for="question in displayQuestions" :key="'max-' + question.id" class="text-center align-middle">{{ formatScore(question.maximum_mark) }}</th>
                <th colspan="2"></th>
              </tr>
              <tr>
                <th colspan="2" class="align-middle">CO Mapping</th>
                <th v-for="question in displayQuestions" :key="'co-' + question.id" class="text-center align-middle">{{ question.course_outcome_codes || "-" }}</th>
                <th colspan="2"></th>
              </tr>
              <!-- Module and Bloom's Taxonomy commented -->
              <!--
              <tr>
                <th colspan="2">Module</th>
                <th v-for="question in questions" :key="'module-' + question.id">{{ question.module_name || "-" }}</th>
                <th colspan="2"></th>
              </tr>
              <tr>
                <th colspan="2">Blooms Taxonomy</th>
                <th v-for="question in questions" :key="'taxonomy-' + question.id">{{ question.blooms_taxonamy_level || "-" }}</th>
                <th colspan="2"></th>
              </tr>
              -->
              <tr>
                <th style="min-width:110px">Roll No.</th>
                <th style="min-width:160px">Name</th>
                <th v-for="question in displayQuestions" :key="'score-' + question.id" class="text-center" style="min-width:90px">Score</th>
                <th class="text-center" style="min-width:110px">Total Score ({{ formatScore(totalMaxMark) }})</th>
                <th class="text-center" style="min-width:100px">Percentage</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="student in students" :key="student.student_id">
                <td class="align-middle">{{ student.roll_number || "-" }}</td>
                <td class="align-middle">{{ student.name }}</td>
                <td v-for="question in displayQuestions" :key="question.id">
                  <b-form-input
                    style="max-width:130px"
                    type="text"
                    inputmode="decimal"
                    size="sm"
                    :disabled="resultPublished"
                    v-model="scoresByKey[scoreKey(question.id, student.student_id)]"
                    :class="{ 'is-invalid': validateScoreInput(question, student.student_id), 'text-center': true }"
                    @blur="onScoreBlur(question, student.student_id)"
                  />
                </td>
                <td class="text-center align-middle font-weight-bold">{{ formatScore(studentTotal(student.student_id)) }}</td>
                <td class="text-center align-middle font-weight-bold">{{ studentPercentage(student.student_id) }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="!resultPublished" class="d-flex justify-content-end mt-2">
          <b-button variant="primary" size="sm" :disabled="saving" @click="save">
            {{ saving ? "Saving..." : "Save Scores" }}
          </b-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
const AUTO_SAVE_EVERY = 10;

export default {
  name: "FacultyCoeEvaluateStudents",
  props: {
    examId: { type: Number, required: true },
    groupedCourseId: { type: Number, required: true },
    evaluatorNumber: { type: Number, required: true }
  },
  data() {
    return {
      fetchData: false,
      errorMessage: "",
      questions: [],
      students: [],
      resultPublished: false,
      scoresByKey: {},
      saving: false,
      // auto-save after every AUTO_SAVE_EVERY changed scores
      lastValuesByKey: {},
      changeCount: 0
    };
  },
  computed: {
    // Questions with subquestions aren't scored on their own row — only their
    // subquestions (5.1, 5.2, ...) get a column; the parent's own "5" column
    // would just be a dead input that never feeds into the total.
    displayQuestions() {
      return this.questions.filter(q => !this.hasSubQuestions(q.id));
    },
    totalMaxMark() {
      let total = 0;
      const processedGroups = {};

      this.questions.forEach(question => {
        if (question.subquestion_parent_id == null && question.orquestion_group_id == null) {
          if (this.hasSubQuestions(question.id)) {
            this.getSubQuestions(question.id).forEach(subQuestion => {
              total += Number(subQuestion.maximum_mark || 0);
            });
          } else {
            total += Number(question.maximum_mark || 0);
          }
        }
      });

      this.questions.forEach(question => {
        const groupId = question.orquestion_group_id;
        if (groupId == null || processedGroups[groupId]) return;

        processedGroups[groupId] = true;
        const groupQuestions = this.questions.filter(q => Number(q.orquestion_group_id) === Number(groupId));
        const bestOf = Number(question.best_of_questions || 1);

        const questionMarks = groupQuestions.map(questionObj => {
          if (this.hasSubQuestions(questionObj.id)) {
            return this.getSubQuestions(questionObj.id).reduce((sum, subQuestion) => {
              return sum + Number(subQuestion.maximum_mark || 0);
            }, 0);
          }
          return Number(questionObj.maximum_mark || 0);
        });

        questionMarks.sort((a, b) => b - a);
        total += questionMarks.slice(0, bestOf).reduce((sum, mark) => sum + mark, 0);
      });

      return total;
    }
  },
  async mounted() {
    await this.fetchScores();
  },
  methods: {
    formatScore(value) {
      if (value === null || value === undefined || value === "") return "";
      const number = Number(value);
      if (Number.isNaN(number)) return "";
      if (Number.isInteger(number)) return String(number);
      return number.toFixed(2);
    },

    scoreKey(examquestionId, studentId) {
      return `${examquestionId}-${studentId}`;
    },

    hasSubQuestions(questionId) {
      return this.questions.some(q => Number(q.subquestion_parent_id) === Number(questionId));
    },

    getSubQuestions(questionId) {
      return this.questions.filter(q => Number(q.subquestion_parent_id) === Number(questionId));
    },

    getQuestionScore(questionId, studentId) {
      const value = this.scoresByKey[this.scoreKey(questionId, studentId)];
      if (value === null || value === undefined || value === "") return 0;
      return Number(value) || 0;
    },

    updateScore(questionId, studentId, value) {
      const key = this.scoreKey(questionId, studentId);
      if (value === "") {
        this.$set(this.scoresByKey, key, "");
        return;
      }
      if (!/^\d*\.?\d*$/.test(value)) return;
      this.$set(this.scoresByKey, key, value);
    },

    handleScoreEntry(question, studentId) {
      const key = this.scoreKey(question.id, studentId);
      const value = this.scoresByKey[key];
      if (value === "" || value === null || value === undefined) return;

      const number = Number(value);

      if (Number.isNaN(number)) {
        this.$set(this.scoresByKey, key, "");
        return;
      }

      if (number < 0) {
        this.$set(this.scoresByKey, key, "0");
        return;
      }

      if (number > Number(question.maximum_mark)) {
        this.$set(this.scoresByKey, key, this.formatScore(question.maximum_mark));
        return;
      }

      this.$set(this.scoresByKey, key, this.formatScore(number));
    },

    onScoreBlur(question, studentId) {
      this.handleScoreEntry(question, studentId);

      const key = this.scoreKey(question.id, studentId);
      const value = this.scoresByKey[key] || "";
      if (value === (this.lastValuesByKey[key] || "")) return;

      this.lastValuesByKey[key] = value;
      this.changeCount++;
      if (this.changeCount >= AUTO_SAVE_EVERY && !this.saving) {
        this.save(true);
      }
    },

    validateScoreInput(question, studentId) {
      const value = this.scoresByKey[this.scoreKey(question.id, studentId)];
      if (value === null || value === undefined || value === "") return false;
      return Number(value) > Number(question.maximum_mark);
    },

    studentTotal(studentId) {
      let total = 0;
      const processedGroups = {};

      // Normal questions
      this.questions.forEach(question => {
        if (question.subquestion_parent_id == null && question.orquestion_group_id == null) {
          if (this.hasSubQuestions(question.id)) {
            this.getSubQuestions(question.id).forEach(subQuestion => {
              total += this.getQuestionScore(subQuestion.id, studentId);
            });
          } else {
            total += this.getQuestionScore(question.id, studentId);
          }
        }
      });

      // OR question groups
      this.questions.forEach(question => {
        const groupId = question.orquestion_group_id;
        if (groupId == null || processedGroups[groupId]) return;

        processedGroups[groupId] = true;

        const groupQuestions = this.questions.filter(
          q => Number(q.orquestion_group_id) === Number(groupId)
        );

        const studentGroupScores = [];

        groupQuestions.forEach(questionObj => {
          let questionScore = 0;

          if (this.hasSubQuestions(questionObj.id)) {
            this.getSubQuestions(questionObj.id).forEach(subQuestion => {
              questionScore += this.getQuestionScore(subQuestion.id, studentId);
            });
          } else {
            questionScore = this.getQuestionScore(questionObj.id, studentId);
          }

          studentGroupScores.push(questionScore);
        });

        studentGroupScores.sort((a, b) => b - a);

        const bestOf = Number(question.best_of_questions || 1);
        const bestScores = studentGroupScores.slice(0, bestOf);

        total += bestScores.reduce((sum, score) => sum + score, 0);
      });

      return total;
    },

    studentPercentage(studentId) {
      if (!this.totalMaxMark) return "0.0";
      return ((this.studentTotal(studentId) / this.totalMaxMark) * 100).toFixed(1);
    },

    async fetchScores() {
      this.fetchData = true;
      this.errorMessage = "";

      try {
        const mainAPI = this.$store.getters.getAPIKey.mainAPI;
        const res = await this.$axios.get(
          `${mainAPI}/faculty/evaluations/${this.examId}/${this.groupedCourseId}/${this.evaluatorNumber}/scores`
        );

        this.questions = res.data.data.questions || [];
        this.students = res.data.data.students || [];
        this.resultPublished = !!res.data.data.result_published;

        const scoresByKey = {};

        (res.data.data.scores || []).forEach(s => {
          const key = this.scoreKey(s.examquestion_id, s.student_id);
          scoresByKey[key] = this.formatScore(s.score);
        });

        this.scoresByKey = scoresByKey;
        this.lastValuesByKey = { ...scoresByKey };
        this.changeCount = 0;
      } catch (error) {
        console.error(error);
        this.errorMessage = "Failed to load evaluation scores.";
      } finally {
        this.fetchData = false;
      }
    },

    async save(isAutoSave = false) {
      this.saving = true;
      this.errorMessage = "";
      // changes made while this save is in flight count towards the next auto-save
      const savedChangeCount = this.changeCount;

      const scores = [];

      this.questions.forEach(question => {
        this.students.forEach(student => {
          const key = this.scoreKey(question.id, student.student_id);
          const value = this.scoresByKey[key];

          scores.push({
            examquestion_id: question.id,
            student_id: student.student_id,
            score: value === "" || value === undefined || value === null ? null : value
          });
        });
      });

      const mainAPI = this.$store.getters.getAPIKey.mainAPI;

      try {
        await this.$axios.post(
          `${mainAPI}/faculty/evaluations/${this.examId}/${this.groupedCourseId}/${this.evaluatorNumber}/scores`,
          { scores }
        );

        this.changeCount = Math.max(0, this.changeCount - savedChangeCount);
        this.$toast.success(isAutoSave ? "Scores auto-saved." : "Scores saved.", { duration: 3000 });
      } catch (error) {
        console.error(error);
        this.errorMessage = isAutoSave ? "Auto-save failed. Please click Save Scores." : "Failed to save scores.";
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.evaluate-table tbody tr:nth-of-type(odd) {
  background-color: #f8f9fa;
}
</style>